import * as signalR from '@aspnet/signalr';
import { action, observable } from 'mobx';

import { ComponentUrls } from '../AppConstants';
import { appStore } from '@app/Stores/AppStore';

import { NotificationHandler } from '../Components/ToastNotification';
import ApiService from './ApiService';
import { HttpTransportType } from '@aspnet/signalr';
import { getEncodedToken } from '@app/Helpers/JWTHelper';
import { SignalRConnectionWrapper } from '@app/Models/SignalRConnectionWrapper';

export type SignalRConnectionState = 'connected' | 'reconnecting';

type TPayloadEvent<T> = (eventName: string, payload: T) => void;
type TEvent = () => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SignalREventHandler = (...args: any[]) => void;

interface ISignalRObservable {
    subscribe<T>(eventName: string, observer: TPayloadEvent<T>): void;
    unsubscribe<T>(eventName: string, observer: TPayloadEvent<T>): void;
}

type SignalRObserver = {
    eventName: string;
    observer: TEvent;
    internalObserver: SignalREventHandler;
};

class SignalRService implements ISignalRObservable {
    @observable public connectionState: SignalRConnectionState = 'reconnecting';
    private _hubConnection: signalR.HubConnection | null;
    private _signalRObservers: SignalRObserver[] = [];
    private _initObservers: (() => void)[] = [];
    private _waitForConnectionPromise: Promise<void> | null;
    private _connectionWrapper: SignalRConnectionWrapper | null = null;
    private _reconnectTimeoutId?: number;

    constructor() {
        document.addEventListener('DOMContentLoaded', this.initialize);
    }

    initialize = async () => {
        if (appStore.isAuthorized()) {
            await this.setUpHub();
        }
    };

    private _jwtTokenFactory() {
        return new Promise<string>((resolve, reject) => {
            if (appStore.currentTokenObj) {
                const timeMsNow = Date.now() / 1000;
                if (appStore.currentTokenObj.exp < timeMsNow) {
                    reject(new Error(`Jwt token has expired at ${appStore.currentTokenObj.exp}, current time is ${timeMsNow}`));
                    return;
                }

                resolve(getEncodedToken(appStore.currentToken));
                return;
            }

            reject(new Error(`User isn't authorized, Jwt token is not defined`));
        });
    }
    
    async setUpHub() {
        const connectionUrl = location.origin + ComponentUrls.HubUrl;

        const transportType = HttpTransportType.LongPolling | HttpTransportType.ServerSentEvents | HttpTransportType.WebSockets;
        const hubConnection = new signalR.HubConnectionBuilder().withUrl(connectionUrl, {
            transport: transportType,
            accessTokenFactory: this._jwtTokenFactory
        }).build();

        hubConnection.on('success', (message: string) => {
            console.log('Success: ' + message);
            NotificationHandler.showSuccess(message, 'Success message');
        });

        hubConnection.on('warning', (message: string) => {
            console.log('Warning: ' + message);
            NotificationHandler.showWarning(message, 'Warning message');
        });

        hubConnection.on('error', (message: string) => {
            console.log('Error: ' + message);
            NotificationHandler.showError(message, 'Error');
        });

        hubConnection.on('info', (message: string) => {
            console.log('Info: ' + message);
            NotificationHandler.showInfo(message, 'Info');
        });

        this._signalRObservers.forEach(item => {
            hubConnection.on(item.eventName, item.internalObserver);
        });

        const reconnect = () => {
            this._setConnectionState('reconnecting');

            window.clearTimeout(this._reconnectTimeoutId);
            this._reconnectTimeoutId = window.setTimeout(() => {
                void this.setUpHub();
            }, 5000);
        };

        const oldConnectionWrapper = this._connectionWrapper;
        this._connectionWrapper = new SignalRConnectionWrapper(hubConnection, reconnect);

        try {
            await hubConnection.start();
        } catch (e) {
            console.error(e);
            reconnect();
            return;
        }

        console.log('SignalR connected!');

        await oldConnectionWrapper?.abortConnection();
        this._hubConnection = hubConnection;

        window.clearTimeout(this._reconnectTimeoutId);
        this._reconnectTimeoutId = undefined;
        this._setConnectionState('connected');

        this._initObservers.forEach(cb => cb());
    }

    @action
    private _setConnectionState(state: SignalRConnectionState) {
        this.connectionState = state;
    }

    onInitialized(cb: () => void) {
        this._initObservers.push(cb);
        return () => this._initObservers = this._initObservers.filter(sub => sub !== cb);
    }

    public async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T> {
        await this.waitForConnection();
        if (!this._hubConnection) throw new Error('SignalR connection not established');
        return this._hubConnection.invoke<T>(methodName, ...args);
    }

    public subscribe<T>(eventName: string, observer: TPayloadEvent<T>) {
        const observerItem: SignalRObserver = {
            eventName: eventName,
            observer: observer as TEvent,
            internalObserver: (payload: T) => observer(eventName, ApiService.typeCheck(payload))
        };

        this.waitForConnection().then(() => {
            this._hubConnection?.on(eventName, observerItem.internalObserver);
            this._signalRObservers.push(observerItem);
        }).catch(e => {
            throw e;
        });
    }

    public unsubscribe<T>(eventName: string, observer: TPayloadEvent<T>){
        this.waitForConnection().then(() => {
            const signalRObserver = this._signalRObservers.find((obs) => obs.eventName === eventName && obs.observer === observer);
            if (!signalRObserver) {
                throw new Error(`Registration for ${eventName} is missing`);
            }

            this._removeObserver(signalRObserver);

        }).catch((e) => {
            const signalRObserver = this._signalRObservers.find((obs) => obs.eventName === eventName && obs.observer === observer);
            signalRObserver && this._removeObserver(signalRObserver);
            throw e;
        });
    }
    
    private _removeObserver(event: SignalRObserver) {
        this._hubConnection?.off(event.eventName, event.internalObserver);

        const index = this._signalRObservers.indexOf(event);
        if (index !== -1) {
            this._signalRObservers.splice(index, 1);
        }
    }

    public waitForConnection(timeout: number = 30000) {
        if (this._waitForConnectionPromise)
            return this._waitForConnectionPromise;

        this._waitForConnectionPromise = new Promise((resolve, reject) => {
            let checkIntervalId: number | undefined;

            const timeoutId = window.setTimeout(() => {
                window.clearInterval(checkIntervalId);
                this._waitForConnectionPromise = null;

                reject(new Error('Timeout waiting for signalR connection'));
            }, timeout);

            const checkConnection = () => {
                if (this._hubConnection && this._hubConnection.state === signalR.HubConnectionState.Connected) {
                    window.clearTimeout(timeoutId);
                    checkIntervalId && window.clearInterval(checkIntervalId);
                    this._waitForConnectionPromise = null;

                    resolve();
                    return true;
                }
                return false;
            };

            if (checkConnection())
                return;
            checkIntervalId = window.setInterval(checkConnection, 50);
        });

        return this._waitForConnectionPromise;
    }
}

export const signalRService = new SignalRService();